import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { FilterList, KeyboardArrowDown } from '@mui/icons-material';
import { useBoolean } from 'react-hanger';
import { useAtomValue } from 'jotai';
import type { Campaign, ElasticSearchFilters, PCADisplayType } from 'c-sdk';
import { PCAReportMetric } from 'c-sdk';
import { NetworkRequestState } from 'c-data-layer';
import { MapWidgetReport } from 'c-main/Components/Campaign/CampaignAnalytics/summaryWidgetReports';
import { MapDataType } from 'c-main/Components/Campaign/CampaignReports/types';
import type { TranslationPath } from 'c-translation';
import { useCommonTranslation } from 'c-translation';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { useDateUtils } from 'c-hooks';
import useChartColors from 'c-reports/useChartColors';
import { ReportFieldFilterES } from 'c-main/Components/Campaign/CampaignReports/ReportFieldFilter';
import { TotalsTile } from 'c-main/Components/Campaign/CampaignAnalytics/PCATotalsByMetric';
import {
    mapDataToSqlMapData,
    performanceStatsToOldCampaignStats,
} from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/Transformers';
import { atom_environmentMode } from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import useCombinedReport from './useCombinedReport';
import type { SeriesColors, SeriesLabels } from './CombinedCharts';
import { ChartCard, CombinedWidget } from './CombinedCharts';
import {
    byDayOfWeek,
    byHour,
    byTotalDesc,
    dataThrough,
    DISPLAY_TYPES,
    keyLeavesBy,
    runningTotal,
    splitRows,
    sumStats,
} from './lib';

const p = 'Modules.Main.Campaigns.CombinedReport';

const NO_FILTERS: ElasticSearchFilters = {};

type TypeChoice = 'all' | PCADisplayType;

const CombinedReport: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const chartColors = useChartColors();
    const { formatDateString } = useDateUtils();
    const environment = useAtomValue(atom_environmentMode) || 'live';

    const available = useMemo(
        () =>
            DISPLAY_TYPES.filter(type =>
                (campaign?.pca_display_types?.[environment] ?? []).includes(type),
            ),
        [campaign?.pca_display_types, environment],
    );

    const [fieldFilters, setFieldFilters] = useState<ElasticSearchFilters>(NO_FILTERS);
    const [typeChoice, setTypeChoice] = useState<TypeChoice>('all');
    const filterPanel = useBoolean(false);

    const { data, filterValues, error, isLoading } = useCombinedReport(
        campaign.id,
        available,
        fieldFilters,
    );

    // The Display Type filter switches the whole page, expected included, without a refetch.
    const types = useMemo(
        () => (typeChoice === 'all' ? available : available.filter(x => x === typeChoice)),
        [available, typeChoice],
    );
    const showClassic = types.includes('Classic');
    const showDigital = types.includes('Display');

    const colors: SeriesColors = {
        expected: theme.palette.grey[400],
        classic: chartColors[0],
        digital: chartColors[3],
        both: theme.palette.text.primary,
    };
    const labels: SeriesLabels = {
        expected: t(`${p}.expected`),
        expectedClassic: t(`${p}.expectedClassic`),
        expectedDigital: t(`${p}.expectedDigital`),
        classic: t(`${p}.classic`),
        digital: t(`${p}.digital`),
        total: t(`${p}.total`),
    };
    const campaignEnded = campaign?.date_end
        ? differenceInCalendarDays(new Date(), parseISO(campaign.date_end)) > 0
        : false;

    const view = useMemo(() => {
        if (!data) return null;
        const pick = <K extends keyof NonNullable<(typeof data)['Classic']>['summary']>(key: K) =>
            Object.fromEntries(types.map(x => [x, data[x]?.summary?.[key]]));
        const statsFor = (list: PCADisplayType[]) =>
            performanceStatsToOldCampaignStats(
                sumStats(list.map(x => data[x]?.summary?.campaign_stats)),
            );

        return {
            // Combined first, then each type: a blended figure alone lets one type's
            // over-delivery hide the other's shortfall.
            tiles: [
                ...(types.length > 1
                    ? [{ key: 'combined', title: `${p}.impactsCombined`, stats: statsFor(types) }]
                    : []),
                ...types.map(x => ({
                    key: x,
                    title: x === 'Classic' ? `${p}.impactsClassic` : `${p}.impactsDigital`,
                    stats: statsFor([x]),
                })),
            ],
            dataThrough: dataThrough(pick('daily_stats')),
            running: runningTotal(pick('daily_stats'), { campaignEnded }),
            byOwner: splitRows(pick('display_owner')).sort(byTotalDesc),
            byRegion: splitRows(pick('display_tv_region')).sort(byTotalDesc),
            byDay: splitRows(pick('day_of_week')).sort(byDayOfWeek),
            byHour: splitRows(
                Object.fromEntries(
                    types.map(x => [x, keyLeavesBy(data[x]?.dowHour ?? [], 'hour')]),
                ),
            ).sort(byHour),
            map: mapDataToSqlMapData(PCAReportMetric.Impacts, {
                data: Object.assign({}, ...types.map(x => data[x]?.frames ?? {})),
                filters: null,
            }).reportData,
        };
    }, [data, types, campaignEnded]);

    const fieldFilterCount = Object.values(fieldFilters).filter(v => v?.length).length;

    if (!data && isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }
    if (!available.length) return <Alert severity="info">{t(`${p}.noData`)}</Alert>;
    if (error && !data) return <Alert severity="error">{t(`${p}.loadError`)}</Alert>;
    if (!view) return null;

    const widgetProps = {
        labels,
        colors,
        showClassic,
        showDigital,
        isLoading,
        startDate: campaign?.date_start ? parseISO(campaign.date_start) : undefined,
        endDate: view.dataThrough ? parseISO(view.dataThrough) : undefined,
    };

    return (
        <Stack gap={2} pb={4}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={2}
            >
                <Typography variant="h4">{t(`${p}.title`)}</Typography>
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                    {view.dataThrough && (
                        <Typography variant="body2" color="text.secondary">
                            {t(`${p}.dataThrough`, {
                                date: formatDateString({ date: view.dataThrough }),
                            })}
                        </Typography>
                    )}
                    {available.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="combined-display-type">
                                {t(`${p}.displayType`)}
                            </InputLabel>
                            <Select
                                labelId="combined-display-type"
                                label={t(`${p}.displayType`)}
                                value={typeChoice}
                                onChange={e => setTypeChoice(e.target.value as TypeChoice)}
                            >
                                <MenuItem value="all">{t(`${p}.allTypes`)}</MenuItem>
                                <MenuItem value="Classic">{labels.classic}</MenuItem>
                                <MenuItem value="Display">{labels.digital}</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    <Button
                        onClick={filterPanel.toggle}
                        color={fieldFilterCount > 0 ? 'primary' : 'secondary'}
                    >
                        <FilterList sx={{ mr: 0.5 }} />
                        {t('Modules.Main.Campaigns.Overview.Reports.filterBy')}
                        <KeyboardArrowDown />
                    </Button>
                </Stack>
            </Stack>

            <Collapse in={filterPanel.value}>
                {filterValues && (
                    <ReportFieldFilterES
                        allValuesByField={filterValues}
                        selectedFields={fieldFilters}
                        showCountFieldsDropdown={false}
                        onSearch={next => setFieldFilters(next ?? NO_FILTERS)}
                        onReset={() => setFieldFilters(NO_FILTERS)}
                    />
                )}
            </Collapse>
            {isLoading && <LinearProgress />}
            {/* A failed refresh keeps the last figures; say so rather than look unfiltered. */}
            {error && data && <Alert severity="warning">{t(`${p}.refreshError`)}</Alert>}

            <Box
                sx={{
                    display: 'grid',
                    // One column per tile, so a single type's tile fills the row.
                    gridTemplateColumns: { xs: '1fr', md: `repeat(${view.tiles.length}, 1fr)` },
                    gap: 2,
                }}
            >
                {view.tiles.map(tile => (
                    <TotalsTile
                        key={tile.key}
                        title={tile.title as TranslationPath}
                        actual={tile.stats.impacts}
                        expected={tile.stats.impacts_expected}
                        performance={+tile.stats.impacts_percentage}
                        inSchedule={tile.stats.impacts_in_schedule}
                        outSchedule={tile.stats.impacts_out_schedule}
                        unbooked={tile.stats.impacts_unbooked}
                        showVariance
                    />
                ))}
            </Box>

            <ChartCard title={t(`${p}.runningTotal`)}>
                <CombinedWidget
                    id="combined-running-total"
                    kind="line"
                    rows={view.running.map(r => ({ ...r, x: formatDateString({ date: r.date }) }))}
                    xAxisLabel={t(`${p}.date`)}
                    namePrefix={`${campaign.id}_combined_running_total`}
                    {...widgetProps}
                />
            </ChartCard>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 2,
                }}
            >
                <ChartCard title={t(`${p}.byOwner`)}>
                    <CombinedWidget
                        id="combined-by-owner"
                        kind="bar"
                        rows={view.byOwner.map(r => ({ ...r, x: r.label }))}
                        xAxisLabel={t(`${p}.owner`)}
                        namePrefix={`${campaign.id}_combined_by_owner`}
                        {...widgetProps}
                    />
                </ChartCard>
                <ChartCard title={t(`${p}.byRegion`)}>
                    <CombinedWidget
                        id="combined-by-region"
                        kind="bar"
                        rows={view.byRegion.map(r => ({ ...r, x: r.label }))}
                        xAxisLabel={t(`${p}.region`)}
                        namePrefix={`${campaign.id}_combined_by_region`}
                        {...widgetProps}
                    />
                </ChartCard>
            </Box>

            <ChartCard title={t(`${p}.byDay`)}>
                <CombinedWidget
                    id="combined-by-day"
                    kind="bar"
                    rows={view.byDay.map(r => ({ ...r, x: r.label.slice(0, 3) }))}
                    xAxisLabel={t(`${p}.day`)}
                    namePrefix={`${campaign.id}_combined_by_day`}
                    {...widgetProps}
                />
            </ChartCard>

            <ChartCard title={t(`${p}.byHour`)}>
                <CombinedWidget
                    id="combined-by-hour"
                    kind="bar"
                    rows={view.byHour.map(r => ({ ...r, x: r.label }))}
                    xAxisLabel={t(`${p}.hour`)}
                    namePrefix={`${campaign.id}_combined_by_hour`}
                    {...widgetProps}
                />
            </ChartCard>

            <ChartCard title={t(`${p}.heatmap`)}>
                {/* The Summary's own map heatmap, for whichever types are shown. */}
                <MapWidgetReport
                    widgetId="combined-heatmap"
                    campaignId={campaign.id}
                    data={view.map}
                    mapDataType={MapDataType.HeatActual}
                    mapDisplayMode="heatmap"
                    loadingState={
                        isLoading ? NetworkRequestState.InProgress : NetworkRequestState.Success
                    }
                />
            </ChartCard>
        </Stack>
    );
};

export default CombinedReport;
